/**
 * Thumbnail generation, in the browser.
 *
 * The browser has already decoded the file to show it to the user. Asking the
 * server to decode it again means an image-processing dependency in the
 * container, CPU spent per upload, and a queue backlog on a busy morning,
 * to produce something the client could have made for free.
 *
 * Previews go to a separate, mutable bucket. They are derived and regenerable;
 * the originals bucket has no update or delete policy, deliberately.
 *
 * Failure is always non-fatal. A missing thumbnail is a cosmetic problem; a
 * failed upload is not.
 */

export interface GeneratedPreview {
  readonly blob: Blob;
  readonly width: number;
  readonly height: number;
}

/** Long edge of the generated thumbnail. Enough for a crisp list row on a 2× display. */
const PREVIEW_MAX_EDGE = 512;
const PREVIEW_QUALITY = 0.72;

export async function generatePreview(
  file: File,
  mimeType: string,
): Promise<GeneratedPreview | null> {
  try {
    if (mimeType === 'application/pdf') {
      return await renderPdfFirstPage(file);
    }
    if (mimeType.startsWith('image/')) {
      return await downscaleImage(file);
    }
    // CSV, XLSX and anything else: no visual to make.
    return null;
  } catch {
    // Deliberately swallowed. Every caller treats a null preview as fine.
    return null;
  }
}

/**
 * Downscales using `createImageBitmap`, which decodes off the main thread.
 *
 * HEIC is the interesting case: Safari decodes it natively, other browsers do
 * not. When decoding fails we return null and the file uploads without a
 * thumbnail, which is exactly right, a technician's iPhone photo must not be
 * refused because Chrome cannot draw it.
 */
async function downscaleImage(file: File): Promise<GeneratedPreview | null> {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (bitmap === null) return null;

  try {
    const scale = Math.min(1, PREVIEW_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (context === null) return null;

    context.drawImage(bitmap, 0, 0, width, height);
    const blob = await canvasToWebp(canvas);
    return blob === null ? null : { blob, width, height };
  } finally {
    bitmap.close();
  }
}

/**
 * Renders page one with pdf.js.
 *
 * pdf.js is loaded lazily. It is the largest dependency in the bundle and most
 * sessions never upload a PDF, so paying for it at first render would be a
 * meaningful cost for nothing. The viewer imports the same module, so a user
 * who opens a PDF pays once.
 */
async function renderPdfFirstPage(file: File): Promise<GeneratedPreview | null> {
  const { loadPdfDocument } = await import('../pdf/pdf-loader.js');
  const data = new Uint8Array(await file.arrayBuffer());
  const document_ = await loadPdfDocument(data);

  try {
    const page = await document_.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = Math.min(2, PREVIEW_MAX_EDGE / Math.max(baseViewport.width, baseViewport.height));
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d');
    if (context === null) return null;

    await page.render({ canvas, canvasContext: context, viewport }).promise;
    const blob = await canvasToWebp(canvas);
    return blob === null ? null : { blob, width: canvas.width, height: canvas.height };
  } finally {
    await document_.cleanup();
  }
}

function canvasToWebp(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/webp', PREVIEW_QUALITY);
  });
}

/** Page count, read while the PDF is already open. Null for anything else. */
export async function readPdfPageCount(file: File, mimeType: string): Promise<number | null> {
  if (mimeType !== 'application/pdf') return null;
  try {
    const { loadPdfDocument } = await import('../pdf/pdf-loader.js');
    const document_ = await loadPdfDocument(new Uint8Array(await file.arrayBuffer()));
    const count = document_.numPages;
    await document_.cleanup();
    return count;
  } catch {
    return null;
  }
}
